/**
 * <nosto-dynamic-card> — custom element override
 *
 * Defines the element BEFORE Nosto's bundle gets a chance to. Custom
 * element names can only be registered once; first define wins, so this
 * file replaces the default `@nosto/web-components` implementation with
 * one tuned for the Coucou Suzette / Dawn integration:
 *
 *   • Fetch `/products/{handle}?section_id=card-product-nosto`
 *   • Concurrency queue: 5 in-flight on mobile, 10 on desktop
 *   • In-memory cache by handle (single Map shared across instances)
 *   • Viewport-priority ordering when `lazy` mode is on
 *   • 429 / 5xx → exponential backoff retry, max 3 attempts
 *   • 404      → element gets `hidden`, parent `<li>` hidden
 *   • Preserves outer height during refetch to prevent CLS
 *
 * Smala had a similar override (see _nosto/docs/04-web-components.md
 * in the smala repo) — this is the Dawn / Nosto-Search-Templates port.
 *
 * Loaded synchronously in `<head>` BEFORE `content_for_header` so that
 * any `customElements.define('nosto-dynamic-card', ...)` call from the
 * Nosto bundle later is a no-op (we keep ownership of the element).
 */
(function () {
	if (typeof customElements === 'undefined') return;
	if (customElements.get('nosto-dynamic-card')) return;

	// ─── shared state (one per page) ─────────────────────────────────────
	var cache = new Map(); // handle -> resolved HTML
	var pending = new Map(); // handle -> Promise<HTML>
	var queue = []; // [{ priority, run }]
	var running = 0;
	var rateLimitedUntil = 0;

	function isMobile() {
		return window.matchMedia('(max-width: 749px)').matches;
	}
	function maxConcurrency() {
		return isMobile() ? 5 : 10;
	}

	function pump() {
		while (running < maxConcurrency() && queue.length) {
			queue.sort(function (a, b) { return b.priority - a.priority; });
			var task = queue.shift();
			running++;
			Promise.resolve()
				.then(task.run)
				.finally(function () { running--; pump(); });
		}
	}

	function schedule(priority, run) {
		queue.push({ priority: priority, run: run });
		pump();
	}

	function sleep(ms) { return new Promise(function (r) { setTimeout(r, ms); }); }

	// Promotion des images lazy du theme (attributs `lazy-src` / `lazy-srcset`)
	// vers `src` / `srcset`. card-product rend la 2e image (hover) avec ces
	// attributs custom, normalement swappes par l'observer lazy natif — qui ne
	// voit pas nos cartes injectees. Sans ca, la 2e image ne se charge jamais.
	function promoteLazyImages(root) {
		var imgs = root.querySelectorAll('img[lazy-src], img[lazy-srcset]');
		for (var i = 0; i < imgs.length; i++) {
			var img = imgs[i];
			var ls = img.getAttribute('lazy-src');
			var lss = img.getAttribute('lazy-srcset');
			if (lss) { img.setAttribute('srcset', lss); img.removeAttribute('lazy-srcset'); }
			if (ls) { img.setAttribute('src', ls); img.removeAttribute('lazy-src'); }
		}
	}

	// PERF : on ne promeut PAS les images lazy (2e image hover) en eager pour
	// toutes les cartes — ce serait 2x les requetes images d'un coup. On utilise
	// un observer partage (rootMargin genereux) qui ne promeut une carte que
	// lorsqu'elle approche du viewport, exactement comme le lazy-load natif.
	var lazyImgObserver = null;
	function getLazyImgObserver() {
		if (lazyImgObserver || typeof IntersectionObserver === 'undefined') return lazyImgObserver;
		lazyImgObserver = new IntersectionObserver(function (entries) {
			for (var i = 0; i < entries.length; i++) {
				if (entries[i].isIntersecting) {
					var el = entries[i].target;
					lazyImgObserver.unobserve(el);
					promoteLazyImages(el);
				}
			}
		}, { rootMargin: '300px' });
		return lazyImgObserver;
	}
	function lazyPromoteWhenVisible(root) {
		var obs = getLazyImgObserver();
		// `root` est le <nosto-dynamic-card> en display:contents (AUCUNE boite)
			// -> un IntersectionObserver pose dessus ne se declenche JAMAIS. On
			// observe donc le .card-wrapper (enfant avec une boite).
			var target = root.querySelector('.card-wrapper') || root.firstElementChild;
			if (!obs || !target) { promoteLazyImages(root); return; }
			obs.observe(target);
	}

	async function fetchSection(handle, sectionId, attempt) {
		sectionId = sectionId || 'card-product-nosto';
		attempt = attempt || 0;
		var now = Date.now();
		if (now < rateLimitedUntil) await sleep(rateLimitedUntil - now);

		// Prefixe racine locale-aware : sur le marche EN, Shopify.routes.root
		// vaut `/en/` -> on fetch `/en/products/...` pour recuperer le HTML EN
		// (titres + URLs produit EN). Sans ca, une PLP EN afficherait des cartes
		// au contenu FR (noms et liens produits FR).
		var localeRoot = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
		var url = localeRoot + 'products/' + encodeURIComponent(handle) + '?section_id=' + encodeURIComponent(sectionId);
		// En preview/dev (theme non publie), l'API Section Rendering rend par defaut
		// contre le theme PUBLIE (404 si la section n'y existe pas encore). On forwarde
		// l'id du theme previsualise pour rendre contre le bon theme. Inoffensif en prod
		// (role 'main' -> aucun ajout).
		try {
			var th = window.Shopify && window.Shopify.theme;
			if (th && th.id && th.role && th.role !== 'main') {
				url += '&preview_theme_id=' + th.id + '&_fd=0&_ab=0';
			}
		} catch (e) {}
		var resp = await fetch(url, { credentials: 'same-origin' });

		if (resp.status === 429 || resp.status >= 500) {
			rateLimitedUntil = Date.now() + 3000;
			if (attempt < 3) {
				await sleep(Math.min(2000 * Math.pow(2, attempt), 15000));
				return fetchSection(handle, sectionId, attempt + 1);
			}
			throw new Error('rate-limited');
		}
		if (resp.status === 404) throw Object.assign(new Error('404'), { code: 404 });
		if (!resp.ok) throw new Error('HTTP ' + resp.status);
		var html = await resp.text();
		// Defensive: section endpoint should return raw section HTML, not a
		// full document. Strip any accidental `<!doctype>` wrappers.
		if (/^<!doctype/i.test(html.trim())) {
			throw new Error('section endpoint returned a full page');
		}
		return html;
	}

	function fetchOnce(handle, sectionId) {
		sectionId = sectionId || 'card-product-nosto';
		// Cle composite section+handle : une meme fiche produit rend un markup
		// DIFFERENT selon la section (grille collection vs carte compacte
		// predictive), donc on ne peut pas mutualiser le cache par handle seul.
		var key = sectionId + ' ' + handle;
		var hit = cache.get(key);
		if (hit) return Promise.resolve(hit);
		var inFlight = pending.get(key);
		if (inFlight) return inFlight;
		var p = fetchSection(handle, sectionId).then(function (html) {
			cache.set(key, html);
			pending.delete(key);
			return html;
		}, function (err) {
			pending.delete(key);
			throw err;
		});
		pending.set(key, p);
		return p;
	}

	// ─── element class ───────────────────────────────────────────────────
	class NostoDynamicCard extends HTMLElement {
		static get observedAttributes() {
			return ['handle', 'section', 'variant-id', 'lazy'];
		}

		// Property setters mirror attributes — Preact / web-components
		// frameworks call setters when they pass props.
		get handle() { return this.getAttribute('handle') || ''; }
		set handle(v) { v ? this.setAttribute('handle', v) : this.removeAttribute('handle'); }
		get section() { return this.getAttribute('section') || ''; }
		set section(v) { v ? this.setAttribute('section', v) : this.removeAttribute('section'); }
		get lazy() { return this.hasAttribute('lazy'); }
		set lazy(v) { v ? this.setAttribute('lazy', '') : this.removeAttribute('lazy'); }

		connectedCallback() {
			if (this._mounted) return;
			this._mounted = true;
			// Show skeleton placeholder immediately so the grid cell has
			// the correct height before the section markup arrives —
			// prevents CLS as cards stream in. The skeleton is replaced
			// by the real card-product HTML once fetch resolves.
			if (!this.innerHTML.trim()) this._renderSkeleton();
			if (this.lazy) this._observeViewport();
			else this._render();
		}

		_renderSkeleton() {
			// Bloc PRIX seul (RecoCard : image + titre rendus en React, seul le prix
			// est fetché) → skeleton minimal (une petite ligne), surtout PAS le gros
			// skeleton carte carré qui casserait le layout compact.
			if (this.section === 'card-product-predictive-price-nosto') {
				this.innerHTML =
					'<span class="predictive-card__price"><span class="nosto-skel-line" style="width:5rem"></span></span>';
				return;
			}
			// Carte compacte predictive : le skeleton collection (vertical, haut)
			// ne convient pas, et ses classes `.nosto-skeleton__*` ne sont chargees
			// que sur les pages collection (main-collection-nosto.liquid) alors que
			// le dropdown s'affiche sur N'IMPORTE quelle page. On rend donc un
			// skeleton compact AUTONOME (styles inline) : vignette + 2 lignes.
			if (this.section === 'card-product-predictive-nosto') {
				// Skeleton animé (shimmer) qui RÉUTILISE la structure `.predictive-card`
				// (media + body + info + prix) → il hérite EXACTEMENT du layout de la
				// vraie carte (row/colonne via container query, padding, hauteur), donc
				// même hauteur → pas de saut au swap skeleton→carte. Les placeholders
				// shimmer sont stylés dans nosto-autocomplete.css (`.nosto-predictive-skeleton`).
				this.innerHTML =
					'<div class="predictive-card nosto-predictive-skeleton" aria-hidden="true">' +
					'<span class="predictive-card__media"></span>' +
					'<span class="predictive-card__body">' +
					'<span class="predictive-card__info">' +
					'<span class="nosto-skel-line" style="width:80%"></span>' +
					'<span class="nosto-skel-line nosto-skel-line--sm" style="width:40%"></span>' +
					'</span>' +
					'<span class="predictive-card__price"><span class="nosto-skel-line" style="width:5rem"></span></span>' +
					'</span>' +
					'</div>';
				return;
			}
			// Mirrors Dawn's `card-wrapper > card > card__inner` with a
			// pulsing grey block; classes `nosto-skeleton__cell` /
			// `__line` reuse the same animation as the Liquid section
			// skeleton (defined in main-collection-nosto.liquid).
			//
			// IMPORTANT anti-CLS : on réserve la hauteur COMPLETE d'une vraie
			// card-product-nosto (image carrée + vendor + titre + avis + tags
			// + prix + bouton « Ajouter au panier »), pas seulement l'image +
			// 2 lignes. Sinon la grille est trop courte au swap skeleton→Nosto
			// puis grandit quand chaque card résout son fetch → ~0.12 de CLS.
			this.innerHTML =
				'<div class="card-wrapper product-card-wrapper nosto-card-skeleton" aria-hidden="true">' +
				'<div class="nosto-skeleton__cell"></div>' +
				'<div class="nosto-skeleton__info">' +
				'<div class="nosto-skeleton__line nosto-skeleton__line--vendor"></div>' +
				'<div class="nosto-skeleton__line"></div>' +
				'<div class="nosto-skeleton__line nosto-skeleton__line--short"></div>' +
				'<div class="nosto-skeleton__line nosto-skeleton__line--rating"></div>' +
				'<div class="nosto-skeleton__line nosto-skeleton__line--price"></div>' +
				'<div class="nosto-skeleton__btn"></div>' +
				'</div>' +
				'</div>';
		}

		disconnectedCallback() {
			if (this._observer) {
				this._observer.disconnect();
				this._observer = null;
			}
			// Preserve height if briefly disconnected (e.g. Preact re-render)
			// to avoid CLS jumps.
			var rect = this.getBoundingClientRect();
			if (rect.height > 0) this.style.minHeight = rect.height + 'px';
		}

		attributeChangedCallback(name, oldVal, newVal) {
			if (oldVal === newVal) return;
			if (this._mounted && (name === 'handle' || name === 'section')) {
				this._render();
			}
		}

		_observeViewport() {
			var self = this;
			this._observer = new IntersectionObserver(function (entries) {
				for (var i = 0; i < entries.length; i++) {
					if (entries[i].isIntersecting) {
						self._observer.disconnect();
						self._observer = null;
						self._render();
						return;
					}
				}
			}, { rootMargin: isMobile() ? '800px' : '1500px' });
			this._observer.observe(this);
		}

		async _render() {
			var handle = this.handle;
			var section = this.section;
			if (!handle || !section) return;

			// Compute priority: in-viewport gets +100, top-of-page gets +50
			var rect = this.getBoundingClientRect();
			var priority = (rect.top < window.innerHeight ? 100 : 0) + Math.max(0, 50 - Math.abs(rect.top) / 100);
			var self = this;

			schedule(priority, function () {
				return fetchOnce(handle, section).then(function (html) {
					if (!self.isConnected) return;
					// Garde-fou : une section peut répondre 200 mais VIDE (produit
					// introuvable via le handle) → carte blanche. On détecte l'absence
					// de vrai markup de carte et on masque comme un 404.
					if (!/predictive-card|predictive-search__item|card-wrapper|recommandations-cart-card/.test(html)) {
						self.style.display = 'none';
						var emptyLi = self.closest('li.grid__item, li.predictive-search__list-item');
						if (emptyLi) emptyLi.style.display = 'none';
						return;
					}
					self.innerHTML = html;
					// La carte est injectee HORS du flux de lazy-load du theme : la
					// 2e image (hover) de card-product utilise `lazy-src`/`lazy-srcset`
					// que l'observer lazy natif ne promeut jamais ici. On la promeut
					// via NOTRE observer (just-in-time pres du viewport) pour ne pas
					// charger 2x les images de toutes les cartes d'un coup (perf).
					lazyPromoteWhenVisible(self);
					self.style.minHeight = ''; // release reservation
					self.dispatchEvent(new CustomEvent('nosto-card:rendered', { bubbles: true, detail: { handle: handle } }));
				}, function (err) {
					if (!self.isConnected) return;
					if (err && err.code === 404) {
						self.style.display = 'none';
						var li = self.closest('li.grid__item, li.predictive-search__list-item');
						if (li) li.style.display = 'none';
					}
					self.dispatchEvent(new CustomEvent('nosto-card:error', { bubbles: true, detail: { handle: handle, error: String(err) } }));
				});
			});
		}
	}

	customElements.define('nosto-dynamic-card', NostoDynamicCard);

	// Expose for prefetching from other code (e.g. autocomplete results
	// preloading the SERP cards).
	window.NostoDynamicCard = NostoDynamicCard;
	window.NostoDynamicCard.prefetch = function (handles) {
		(handles || []).forEach(function (h) {
			schedule(0, function () { return fetchOnce(h).catch(function () {}); });
		});
	};

	// ─── Avis : re-init des badges apres injection client-side ──────────
	// La grille Nosto est injectee APRES le scan initial du widget d'avis,
	// donc les badges des cartes ne sont jamais rendus : on relance
	// `refreshJudgemeWidgets()` apres rendu. Debounce : une seule passe pour
	// tout un lot de cartes (l'event `nosto-card:rendered` fire 1x par carte).
	var reviewsDebounce = null;
	document.addEventListener('nosto-card:rendered', function () {
		if (reviewsDebounce) clearTimeout(reviewsDebounce);
		reviewsDebounce = setTimeout(function () {
			// Judge.me : le retry est gere par refreshJudgemeWidgets()
			if (typeof window.refreshJudgemeWidgets === 'function') window.refreshJudgemeWidgets();
		}, 300);
	});
})();
